import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function MarketPlacerAddEdit() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        nickname: "",
        phone: "",
        password: "",
        password_confirmation: "",
        father_name: "",
        gender: "",
        dob: "",
        cnic: "",
        whatsapp: "",
        house_address: "",
        bank_name: "",
        bank_account_title: "",
        bank_account_number: "",
        gmail: "",
        ebay_username: "",
        payoneer_username: "",
        paypal_username: "",
        reference: "",
        account_date: "",
        account_status: true,
        account_message: "",
        opening_balance: 0,
        opbs: 0,
        share_percentage: 0,
        shipping_only: false,
        listing_limit: 0,
        remaining_limit: 0,
        dollar_limit: 0,
    });

    const [files, setFiles] = useState({
        cnic_front_image: null,
        cnic_back_image: null,
        atm_front_image: null,
        atm_back_image: null,
        bank_statement: null,
        user_image: null,
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [pageLoading, setPageLoading] = useState(isEdit && !location.state?.marketPlacer);
    const [alert, setAlert] = useState(null);

    const populateForm = (item) => {
        const profile = item.mplacer || {};
        setFormData({
            name: item.name || "",
            email: item.email || "",
            nickname: item.nickname || "",
            phone: item.phone || "",
            password: "",
            password_confirmation: "",
            father_name: profile.father_name || "",
            gender: profile.gender || "",
            dob: profile.dob || "",
            cnic: profile.cnic || "",
            whatsapp: profile.whatsapp || "",
            house_address: profile.house_address || "",
            bank_name: profile.bank_name || "",
            bank_account_title: profile.bank_account_title || "",
            bank_account_number: profile.bank_account_number || "",
            gmail: profile.gmail || "",
            ebay_username: profile.ebay_username || "",
            payoneer_username: profile.payoneer_username || "",
            paypal_username: profile.paypal_username || "",
            reference: profile.reference || "",
            account_date: profile.account_date || "",
            account_status: profile.account_status ?? true,
            account_message: profile.account_message || "",
            opening_balance: profile.opening_balance ?? 0,
            opbs: profile.opbs ?? 0,
            share_percentage: profile.share_percentage ?? 0,
            shipping_only: profile.shipping_only ?? false,
            listing_limit: profile.listing_limit ?? 0,
            remaining_limit: profile.remaining_limit ?? 0,
            dollar_limit: profile.dollar_limit ?? 0,
        });
    };

    useEffect(() => {
        if (isEdit) {
            if (location.state?.marketPlacer) {
                populateForm(location.state.marketPlacer);
                setPageLoading(false);
            } else {
                const fetchMarketPlacer = async () => {
                    try {
                        const res = await axios.get(
                            `${process.env.REACT_APP_API_URL}/admin/market-placer`,
                            { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
                        );
                        if (res.data?.success) {
                            const item = res.data.data.find((m) => m.id === parseInt(id));
                            if (item) {
                                populateForm(item);
                            } else {
                                setAlert({ type: "danger", message: "Market Placer not found." });
                            }
                        }
                    } catch (err) {
                        setAlert({ type: "danger", message: "Failed to load details." });
                    } finally {
                        setPageLoading(false);
                    }
                };
                fetchMarketPlacer();
            }
        }
    }, [id, isEdit, location.state]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleFileChange = (e) => {
        const { name, files: selectedFiles } = e.target;
        if (selectedFiles && selectedFiles[0]) {
            setFiles((prev) => ({ ...prev, [name]: selectedFiles[0] }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});
        setAlert(null);

        const payload = new FormData();
        Object.keys(formData).forEach((key) => {
            if (formData[key] !== null && formData[key] !== "") {
                payload.append(key, typeof formData[key] === "boolean" ? (formData[key] ? "1" : "0") : formData[key]);
            }
        });

        Object.keys(files).forEach((key) => {
            if (files[key]) payload.append(key, files[key]);
        });

        let url = `${process.env.REACT_APP_API_URL}/admin/market-placer`;
        if (isEdit) {
            url = `${process.env.REACT_APP_API_URL}/admin/market-placer/${id}`;
            payload.append("_method", "PUT");
        }

        try {
            const response = await axios.post(url, payload, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            if (response.data?.success) {
                navigate("/market-placer");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setAlert({
                    type: "danger",
                    message: err.response?.data?.message || "Operation failed. Please try again.",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (pageLoading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("Loading data...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {isEdit ? t("Edit Market Placer") : t("Add Market Placer")}
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">{t("Home")}</Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link to="/admin/market-placer" className="mc-breadcrumb-link">{t("Market Placers")}</Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">{isEdit ? t("Edit") : t("Add")}</li>
                                </ul>
                            </div>
                            <Button variant="secondary" onClick={() => navigate("/market-placer")}>
                                {t("Back")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Header className="bg-white py-3">
                            <h5 className="mb-0 fw-bold">{t("Account Details")}</h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {alert && (
                                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                                    {alert.message}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Full Name *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                placeholder="e.g. John Doe"
                                                value={formData.name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.name}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.name?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Nickname *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="nickname"
                                                placeholder="e.g. Johnny"
                                                value={formData.nickname}
                                                onChange={handleChange}
                                                isInvalid={!!errors.nickname}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.nickname?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Email Address *</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder="e.g. john@example.com"
                                                value={formData.email}
                                                onChange={handleChange}
                                                isInvalid={!!errors.email}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.email?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Phone Number</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phone"
                                                placeholder="e.g. +1234567890"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                isInvalid={!!errors.phone}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{isEdit ? "New Password (Optional)" : "Password *"}</Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="password"
                                                placeholder="Enter password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                isInvalid={!!errors.password}
                                                required={!isEdit}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.password?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{isEdit ? "Confirm New Password" : "Confirm Password *"}</Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="password_confirmation"
                                                placeholder="Re-enter password"
                                                value={formData.password_confirmation}
                                                onChange={handleChange}
                                                required={!isEdit || Boolean(formData.password)}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}><hr className="my-3" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary">{t("Profile Information")}</h6></Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Father Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="father_name"
                                                placeholder="e.g. Richard Doe"
                                                value={formData.father_name}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Gender</Form.Label>
                                            <Form.Select name="gender" value={formData.gender} onChange={handleChange}>
                                                <option value="">Select Gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Date of Birth</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="dob"
                                                value={formData.dob}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>CNIC</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="cnic"
                                                placeholder="e.g. 35201-XXXXXXX-X"
                                                value={formData.cnic}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>WhatsApp Number</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="whatsapp"
                                                placeholder="e.g. +1234567890"
                                                value={formData.whatsapp}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Gmail</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="gmail"
                                                placeholder="e.g. account@gmail.com"
                                                value={formData.gmail}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>House Address</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="house_address"
                                                placeholder="e.g. Street #1, House #2, City, Country"
                                                value={formData.house_address}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}><hr className="my-3" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary">{t("Banking & Marketplace Accounts")}</h6></Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Bank Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="bank_name"
                                                placeholder="e.g. Chase Bank"
                                                value={formData.bank_name}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Bank Account Title</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="bank_account_title"
                                                placeholder="e.g. John Doe"
                                                value={formData.bank_account_title}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Bank Account Number / IBAN</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="bank_account_number"
                                                placeholder="e.g. US12345678901234"
                                                value={formData.bank_account_number}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>eBay Username</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="ebay_username"
                                                placeholder="e.g. ebay_seller_123"
                                                value={formData.ebay_username}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Payoneer Username / ID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="payoneer_username"
                                                placeholder="e.g. payoneer_user"
                                                value={formData.payoneer_username}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>PayPal Username / Email</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="paypal_username"
                                                placeholder="e.g. paypal@example.com"
                                                value={formData.paypal_username}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}><hr className="my-3" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary">{t("Limits & Configurations")}</h6></Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Listing Limit</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="listing_limit"
                                                placeholder="e.g. 500"
                                                value={formData.listing_limit}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Remaining Limit</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="remaining_limit"
                                                placeholder="e.g. 200"
                                                value={formData.remaining_limit}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Dollar Limit ($)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="dollar_limit"
                                                placeholder="e.g. 10000.00"
                                                value={formData.dollar_limit}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Share Percentage (%)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="share_percentage"
                                                placeholder="e.g. 15.00"
                                                value={formData.share_percentage}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}><hr className="my-3" /></Col>
                                    <Col md={12}><h6 className="fw-bold text-primary">{t("Document Attachments")}</h6></Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>User Image</Form.Label>
                                            <Form.Control type="file" name="user_image" onChange={handleFileChange} accept="image/*" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>CNIC Front Image</Form.Label>
                                            <Form.Control type="file" name="cnic_front_image" onChange={handleFileChange} accept="image/*" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>CNIC Back Image</Form.Label>
                                            <Form.Control type="file" name="cnic_back_image" onChange={handleFileChange} accept="image/*" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>ATM Front Image</Form.Label>
                                            <Form.Control type="file" name="atm_front_image" onChange={handleFileChange} accept="image/*" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>ATM Back Image</Form.Label>
                                            <Form.Control type="file" name="atm_back_image" onChange={handleFileChange} accept="image/*" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Bank Statement Document</Form.Label>
                                            <Form.Control type="file" name="bank_statement" onChange={handleFileChange} accept="image/*,application/pdf" />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex gap-2 justify-content-end mt-4">
                                    <Button variant="secondary" onClick={() => navigate("/market-placer")}>
                                        {t("Cancel")}
                                    </Button>
                                    <Button variant="primary" type="submit" disabled={submitting}>
                                        {submitting ? t("Saving...") : isEdit ? t("Update Market Placer") : t("Save Market Placer")}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}